import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppWidget from '../components/WhatsAppWidget';
import { useLanguage } from '../context/LanguageContext';

const API_URL = '';

const VILLA_IMAGE = "https://static.prod-images.emergentagent.com/jobs/38415ed3-3d9c-47e3-b9ab-3231a1029ca0/images/003ca9e39a67672c82fe8280bb3954d4fbaea7ee76692b7ddb0e963ecb37c0bf.png";
const DEFAULT_CH3_IMAGE = "/images/civ_img.png";

const C = { vert: '#1e4a1a', or: '#c4a76c', ivoire: '#faf9f6', anthracite: '#1a1a1a', texte: '#4a5a47', texteMuted: '#8a9585' };

const DEFAULTS = {
  hero: { subtitle_fr: 'Développeur & aménageur territorial.', subtitle_en: 'Territorial developer & planner.' },
  ch1: {
    quote_fr: "One Green Developpement imagine et développe des projets profondément ancrés dans leur territoire, où le paysage façonne l'architecture et où le foncier devient un levier stratégique de création de valeur.",
    quote_en: 'One Green Development imagines and develops projects deeply rooted in their territory, where the landscape shapes architecture and land becomes a strategic lever for value creation.',
    body1_fr: "Nous développons des projets qui ne se limitent pas à bâtir, mais qui organisent, structurent et façonnent durablement leur environnement. Notre démarche conjugue innovation, rigueur opérationnelle et exigence qualitative, afin de créer des destinations durables, pensées pour traverser le temps, à l'intersection de l'aménagement intelligent, de l'expérience de vie et d'un investissement responsable et pérenne.",
    body1_en: "We develop projects that go beyond building — they organize, structure and sustainably shape their environment. Our approach combines innovation, operational rigor and qualitative standards, creating lasting destinations designed to stand the test of time, at the intersection of intelligent planning, living experience and responsible, sustainable investment.",
    body2_fr: "Depuis notre création, nous portons une ambition claire : transformer des territoires en lieux de vie organisés, sécurisés et valorisants, au service d'une croissance maîtrisée.",
    body2_en: 'Since our founding, we carry a clear ambition: transforming territories into organized, secure and valuable living spaces, in service of controlled growth.',
  },
  ch2: {
    expertise_title_fr: "L'expertise au service du territoire", expertise_title_en: 'Expertise serving the territory',
    expertise_body1_fr: "Chez One Green Dev, chaque projet naît d'une lecture attentive du territoire. Le relief, la végétation, les flux naturels de Songon ne sont pas des contraintes mais des atouts qui dictent l'orientation des bâtiments, la circulation des espaces et la palette de matériaux.",
    expertise_body1_en: 'At One Green Dev, every project begins with a careful reading of the territory. The terrain, vegetation, and natural flows of Songon are not constraints but assets that dictate building orientation, space flow, and material choices.',
    expertise_body2_fr: "L'aménagement intelligent signifie intégrer les réalités du terrain pour créer des lieux qui respirent, qui s'inscrivent dans le paysage et valorisent chaque mètre carré avec intention.",
    expertise_body2_en: 'Intelligent development means integrating terrain realities to create spaces that breathe, that fit into the landscape and valorize every square meter with purpose.',
    rigor_title_fr: 'Sécurisation foncière & maîtrise du développement', rigor_title_en: 'Land security & development mastery',
    rigor_body1_fr: "One Green Dev place la sécurisation foncière et juridique au plus haut niveau d'exigence. Notre équipe pluridisciplinaire coordonne l'ensemble des intervenants techniques, financiers et institutionnels dans une logique de qualité, de maîtrise des coûts et de respect des délais.",
    rigor_body1_en: 'One Green Dev places land and legal security at the highest level. Our multidisciplinary team coordinates all technical, financial and institutional stakeholders with a focus on quality, cost control and timeline compliance.',
    rigor_body2_fr: "Nous appliquons des standards internationaux à chaque étape, de la conception initiale jusqu'à la livraison, offrant un cadre d'investissement fiable et transparent.",
    rigor_body2_en: 'We apply international standards at every stage, from initial design to delivery, offering a reliable and transparent investment framework.',
  },
  ch3: {
    image_url: DEFAULT_CH3_IMAGE,
    body1_fr: "Première économie de l'Union Économique et Monétaire Ouest Africaine (UEMOA), la Côte d'Ivoire affiche une croissance soutenue et une stabilité qui en font un hub stratégique pour l'investissement en Afrique subsaharienne.",
    body1_en: 'As the leading economy of the West African Economic and Monetary Union (WAEMU), Ivory Coast displays sustained growth and stability, making it a strategic hub for investment in sub-Saharan Africa.',
    body2_fr: "Le Grand Abidjan concentre l'essentiel du dynamisme économique du pays. Songon, situé dans cette zone d'expansion, bénéficie des projets structurants nationaux et d'un potentiel foncier exceptionnel encore largement inexploité.",
    body2_en: "Greater Abidjan concentrates the country's economic dynamism. Songon, located in this expansion zone, benefits from national structural projects and exceptional, largely untapped land potential.",
    closing_fr: "Investir à Songon aujourd'hui, c'est se positionner sur un territoire en pleine structuration, soutenu par des infrastructures nationales majeures et une demande résidentielle croissante.",
    closing_en: 'Investing in Songon today means positioning yourself on a territory in full development, supported by major national infrastructure and growing residential demand.',
  },
  ogd_figures: [
    { value: '341+', label_fr: 'Hectares', label_en: 'Hectares', sub_fr: 'de foncier maîtrisé', sub_en: 'of controlled land' },
    { value: '5', label_fr: 'Projets', label_en: 'Projects', sub_fr: 'en développement', sub_en: 'in development' },
    { value: '15+', label_fr: 'Experts', label_en: 'Experts', sub_fr: 'mobilisés', sub_en: 'mobilized' },
    { value: '100%', label_fr: 'Engagement', label_en: 'Commitment', sub_fr: 'qualité & durabilité', sub_en: 'quality & sustainability' },
  ],
  country_figures: [
    { value: '8%', label_fr: 'Croissance du PIB', label_en: 'GDP Growth', sub_fr: "parmi les plus fortes d'Afrique", sub_en: 'among the strongest in Africa' },
    { value: '#1', label_fr: 'Économie UEMOA', label_en: 'WAEMU Economy', sub_fr: 'première puissance de la zone', sub_en: 'leading economy in the zone' },
    { value: '6M+', label_fr: 'Grand Abidjan', label_en: 'Greater Abidjan', sub_fr: 'habitants — moteur du pays', sub_en: "inhabitants — the country's engine" },
    { value: '2030', label_fr: 'Vision nationale', label_en: 'National Vision', sub_fr: 'plan de développement structurant', sub_en: 'structural development plan' },
  ],
};

const FadeIn = ({ children, delay = 0, className = '' }) => (
  <motion.div className={className} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.8, delay, ease: [0.25, 0.1, 0.25, 1] }}>
    {children}
  </motion.div>
);

const Label = ({ children }) => (
  <div className="flex items-center gap-3 mb-8">
    <div className="w-10 h-px" style={{ backgroundColor: C.or }} />
    <span className="text-[10px] font-semibold tracking-[0.35em] uppercase" style={{ fontFamily: "'Montserrat', sans-serif", color: C.or }}>{children}</span>
  </div>
);

const ChapterNum = ({ num }) => (
  <span className="text-[11px] font-semibold tracking-[0.2em] uppercase block mb-3" style={{ fontFamily: "'Montserrat', sans-serif", color: C.or, opacity: 0.6 }}>{num}</span>
);

const ADNPage = () => {
  const { language } = useLanguage();
  const fr = language === 'fr';
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const [d, setD] = useState(DEFAULTS);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${API_URL}/api/public/adn-content`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.content) setD(prev => ({ ...prev, ...data.content })); })
      .catch(() => { /* content loaded from defaults */ });
  }, []);

  const t = (section, field) => d?.[section]?.[field + (fr ? '_fr' : '_en')] || DEFAULTS?.[section]?.[field + (fr ? '_fr' : '_en')] || '';
  const figures = (key) => (d?.[key] || DEFAULTS[key] || []).map(f => ({ value: f.value, label: fr ? f.label_fr : f.label_en, sub: fr ? f.sub_fr : f.sub_en }));

  return (
    <div className="min-h-screen" style={{ backgroundColor: C.ivoire }} data-testid="adn-ogd-page">
      <Header />

      {/* HERO */}
      <section ref={heroRef} className="relative h-screen overflow-hidden">
        <motion.div className="absolute inset-0" style={{ y: heroY }}>
          <img src={VILLA_IMAGE} alt="Villa de luxe One Green Dev" className="w-full h-[120%] object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.5) 100%)' }} />
        </motion.div>
        <motion.div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6" style={{ opacity: heroOpacity }}>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.3 }}>
            <div className="flex items-center justify-center gap-3 mb-10">
              <div className="w-10 h-px" style={{ backgroundColor: C.or }} />
              <span className="text-[10px] font-semibold tracking-[0.4em] uppercase" style={{ fontFamily: "'Montserrat', sans-serif", color: C.or }}>{fr ? 'Notre identité' : 'Our identity'}</span>
              <div className="w-10 h-px" style={{ backgroundColor: C.or }} />
            </div>
          </motion.div>
          <motion.h1 className="text-4xl sm:text-5xl lg:text-7xl text-white leading-[1.08] max-w-4xl" style={{ fontFamily: "'Playfair Display', serif" }} initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.4 }}>
            {fr ? <>L'ADN<span className="block mt-3" style={{ color: C.or }}>One Green Dev</span></> : <>The DNA of<span className="block mt-3" style={{ color: C.or }}>One Green Dev</span></>}
          </motion.h1>
          <motion.p className="mt-10 text-base sm:text-lg text-white/60 max-w-lg leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif" }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.7 }}>
            {t('hero', 'subtitle')}
          </motion.p>
        </motion.div>
        <motion.div className="absolute bottom-12 left-1/2 -translate-x-1/2 z-10" animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.5 }}>
          <div className="w-px h-12 mx-auto" style={{ background: `linear-gradient(to bottom, ${C.or}, transparent)` }} />
        </motion.div>
      </section>

      {/* CHAPITRE I — LE MANIFESTE */}
      <section className="py-32 lg:py-44" style={{ backgroundColor: C.ivoire }} data-testid="chapitre-1">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <FadeIn><ChapterNum num={fr ? 'Chapitre I' : 'Chapter I'} /><Label>{fr ? 'Le Manifeste' : 'The Manifesto'}</Label></FadeIn>
          <FadeIn delay={0.15}>
            <h2 className="text-3xl sm:text-4xl lg:text-[2.8rem] leading-[1.3] mb-12" style={{ fontFamily: "'Playfair Display', serif", color: C.vert }}>
              {t('ch1', 'quote')}
            </h2>
          </FadeIn>
          <FadeIn delay={0.25}><div className="w-14 h-px mb-12" style={{ backgroundColor: C.or, opacity: 0.4 }} /></FadeIn>
          <FadeIn delay={0.3}><p className="text-base sm:text-lg leading-[2] mb-8" style={{ fontFamily: "'Montserrat', sans-serif", color: C.texte }}>{t('ch1', 'body1')}</p></FadeIn>
          <FadeIn delay={0.35}><p className="text-base sm:text-lg leading-[2]" style={{ fontFamily: "'Montserrat', sans-serif", color: C.texte }}>{t('ch1', 'body2')}</p></FadeIn>
          <FadeIn delay={0.4}><p className="mt-14 text-xs font-semibold uppercase tracking-[0.25em]" style={{ fontFamily: "'Montserrat', sans-serif", color: C.or }}>&mdash; One Green Dev</p></FadeIn>
        </div>
      </section>

      {/* CHAPITRE II — HÉRITAGE */}
      <section className="py-32 lg:py-44" style={{ backgroundColor: '#fff' }} data-testid="chapitre-2">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-20">
              <ChapterNum num={fr ? 'Chapitre II' : 'Chapter II'} />
              <div className="flex items-center justify-center gap-3 mb-8 mt-4">
                <div className="w-10 h-px" style={{ backgroundColor: C.or, opacity: 0.4 }} />
                <span className="text-[10px] font-semibold tracking-[0.35em] uppercase" style={{ fontFamily: "'Montserrat', sans-serif", color: C.or }}>{fr ? 'Notre expertise' : 'Our Expertise'}</span>
                <div className="w-10 h-px" style={{ backgroundColor: C.or, opacity: 0.4 }} />
              </div>
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-2 gap-16 lg:gap-24">
            {/* Héritage */}
            <FadeIn>
              <div>
                <Label>{fr ? 'Héritage' : 'Heritage'}</Label>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl leading-[1.25] mb-8" style={{ fontFamily: "'Playfair Display', serif", color: C.vert }}>{t('ch2', 'expertise_title')}</h2>
                <p className="text-base leading-[2] mb-6" style={{ fontFamily: "'Montserrat', sans-serif", color: C.texte }}>{t('ch2', 'expertise_body1')}</p>
                <p className="text-base leading-[2]" style={{ fontFamily: "'Montserrat', sans-serif", color: C.texte }}>{t('ch2', 'expertise_body2')}</p>
              </div>
            </FadeIn>
            {/* Rigueur opérationnelle */}
            <FadeIn delay={0.15}>
              <div>
                <Label>{fr ? 'Rigueur opérationnelle' : 'Operational rigor'}</Label>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl leading-[1.25] mb-8" style={{ fontFamily: "'Playfair Display', serif", color: C.vert }}>{t('ch2', 'rigor_title')}</h2>
                <p className="text-base leading-[2] mb-6" style={{ fontFamily: "'Montserrat', sans-serif", color: C.texte }}>{t('ch2', 'rigor_body1')}</p>
                <p className="text-base leading-[2]" style={{ fontFamily: "'Montserrat', sans-serif", color: C.texte }}>{t('ch2', 'rigor_body2')}</p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* OGD Figures */}
      <section className="py-24 lg:py-28" style={{ backgroundColor: C.ivoire }} data-testid="adn-figures">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-16">
              <div className="flex items-center justify-center gap-3 mb-5">
                <div className="w-10 h-px" style={{ backgroundColor: C.or }} />
                <span className="text-[10px] font-semibold tracking-[0.35em] uppercase" style={{ fontFamily: "'Montserrat', sans-serif", color: C.or }}>{fr ? 'One Green Dev en chiffres' : 'One Green Dev in numbers'}</span>
                <div className="w-10 h-px" style={{ backgroundColor: C.or }} />
              </div>
            </div>
          </FadeIn>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {figures('ogd_figures').map((fig, i) => (
              <FadeIn key={`ogd-fig-${fig.value}-${fig.label}`} delay={i * 0.08}>
                <div className="text-center py-10 px-4 rounded-sm" style={{ backgroundColor: '#fff', boxShadow: '0 1px 15px rgba(0,0,0,0.03)' }} data-testid={`figure-${i}`}>
                  <span className="text-4xl sm:text-5xl font-light" style={{ fontFamily: "'Playfair Display', serif", color: C.or }}>{fig.value}</span>
                  <p className="mt-3 text-[11px] font-bold uppercase tracking-[0.15em]" style={{ fontFamily: "'Montserrat', sans-serif", color: C.vert }}>{fig.label}</p>
                  <p className="mt-1 text-[10px]" style={{ fontFamily: "'Montserrat', sans-serif", color: C.texteMuted }}>{fig.sub}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CHAPITRE III — LA DESTINATION */}
      <section className="py-32 lg:py-44" style={{ backgroundColor: C.vert }} data-testid="chapitre-3">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-20">
              <ChapterNum num={fr ? 'Chapitre III' : 'Chapter III'} />
              <div className="flex items-center justify-center gap-3 mb-8 mt-4">
                <div className="w-10 h-px" style={{ backgroundColor: C.or, opacity: 0.4 }} />
                <span className="text-[10px] font-semibold tracking-[0.35em] uppercase" style={{ fontFamily: "'Montserrat', sans-serif", color: C.or }}>{fr ? 'La Destination' : 'The Destination'}</span>
                <div className="w-10 h-px" style={{ backgroundColor: C.or, opacity: 0.4 }} />
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl text-white leading-snug max-w-3xl mx-auto" style={{ fontFamily: "'Playfair Display', serif" }}>
                {fr ? "La Côte d'Ivoire, moteur économique de l'Afrique de l'Ouest" : 'Ivory Coast, economic engine of West Africa'}
              </h2>
            </div>
          </FadeIn>
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center mb-24">
            <FadeIn><img src={d?.ch3?.image_url || DEFAULT_CH3_IMAGE} alt="Côte d'Ivoire" className="w-full rounded-sm" loading="lazy" style={{ aspectRatio: '16/10', objectFit: 'cover' }} data-testid="ch3-image" /></FadeIn>
            <div>
              <FadeIn delay={0.1}><p className="text-base leading-[2] mb-6" style={{ fontFamily: "'Montserrat', sans-serif", color: 'rgba(255,255,255,0.65)' }}>{t('ch3', 'body1')}</p></FadeIn>
              <FadeIn delay={0.2}><p className="text-base leading-[2]" style={{ fontFamily: "'Montserrat', sans-serif", color: 'rgba(255,255,255,0.65)' }}>{t('ch3', 'body2')}</p></FadeIn>
            </div>
          </div>
          <FadeIn>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="country-figures">
              {figures('country_figures').map((fig, i) => (
                <div key={`country-fig-${fig.value}-${fig.label}`} className="text-center py-8 px-4 rounded-sm" style={{ backgroundColor: 'rgba(196,167,108,0.1)', border: '1px solid rgba(196,167,108,0.15)' }} data-testid={`country-figure-${i}`}>
                  <span className="text-3xl sm:text-4xl font-light" style={{ fontFamily: "'Playfair Display', serif", color: C.or }}>{fig.value}</span>
                  <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.12em]" style={{ fontFamily: "'Montserrat', sans-serif", color: 'rgba(255,255,255,0.8)' }}>{fig.label}</p>
                  <p className="mt-1 text-[9px]" style={{ fontFamily: "'Montserrat', sans-serif", color: 'rgba(255,255,255,0.35)' }}>{fig.sub}</p>
                </div>
              ))}
            </div>
          </FadeIn>
          <FadeIn delay={0.15}>
            <div className="mt-20 text-center">
              <p className="text-base leading-[2] max-w-2xl mx-auto" style={{ fontFamily: "'Montserrat', sans-serif", color: 'rgba(255,255,255,0.5)' }}>{t('ch3', 'closing')}</p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* CLOSING */}
      <section className="py-32 lg:py-40" style={{ backgroundColor: C.ivoire }} data-testid="adn-closing">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <FadeIn><div className="w-px h-16 mx-auto mb-12" style={{ background: `linear-gradient(to bottom, transparent, ${C.or}, transparent)` }} /></FadeIn>
          <FadeIn delay={0.1}>
            <p className="text-2xl sm:text-3xl lg:text-4xl leading-[1.4]" style={{ fontFamily: "'Playfair Display', serif", color: C.vert }}>
              {fr ? 'Pour explorer nos projets, découvrez notre Masterplan.' : 'To explore our projects, discover our Masterplan.'}
            </p>
          </FadeIn>
          <FadeIn delay={0.2}>
            <div className="mt-12">
              <Link to="/#masterplan" className="inline-flex items-center gap-3 text-[13px] font-semibold uppercase tracking-[0.15em] transition-all duration-300 group" style={{ fontFamily: "'Montserrat', sans-serif", color: C.or }} data-testid="grand-plan-link">
                {fr ? 'Voir le Masterplan' : 'See the Masterplan'}
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-2" />
              </Link>
            </div>
          </FadeIn>
          <FadeIn delay={0.3}><div className="w-px h-16 mx-auto mt-12" style={{ background: `linear-gradient(to bottom, transparent, ${C.or}, transparent)` }} /></FadeIn>
        </div>
      </section>

      <Footer />
      <WhatsAppWidget />
    </div>
  );
};

export default ADNPage;
